import { FormEvent, useState } from "react"
import styled from "styled-components"

import {
  Dialog,
  Select,
  spacing,
  Switch,
  Text,
} from "@minuthq/meatball-ui-react"

import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchTaskAutomations } from "src/data/taskManagement/taskAutomationQueries"
import { TTaskAutomation } from "src/data/taskManagement/taskAutomationTypes"
import { useFetchTaskTemplates } from "src/data/taskManagement/taskTemplateQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { Divider } from "src/ui/Divider/Divider"
import { Show } from "src/ui/Show/Show"

const FORM_ID = "task-automation"

export function TaskAutomationManageDialog({
  onClose,
  onSubmit,
  taskAutomation,
  title,
  description,
}: {
  onClose: () => void
  onSubmit: (enabled: boolean, templateId: string | undefined) => void
  taskAutomation: TTaskAutomation
  title: string
  description: string
}) {
  const { t, langKeys, tPlaceholder } = useTranslate()

  const { orgId } = useOrganization()

  const fetchTemplates = useFetchTaskTemplates({
    orgId,
  })

  const fetchTaskAutomations = useFetchTaskAutomations({
    orgId,
  })

  const [enabled, setEnabled] = useState(taskAutomation.enabled ?? false)

  const [templateId, setTemplateId] = useState<string | undefined>(
    taskAutomation.template_id
  )

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    onSubmit(enabled, enabled ? templateId : undefined)
    onClose()
  }

  const isLoading = fetchTaskAutomations.isLoading || fetchTemplates.isLoading

  const templateOptions =
    fetchTemplates.data?.templates.map((template) => ({
      label: template.title,
      value: template.template_id,
    })) ?? []

  const emptyOption = {
    label: "",
    value: "",
    hidden: true,
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title={title}
      description={description}
      formId={FORM_ID}
      confirmLabel={t(langKeys.save)}
    >
      <Form id={FORM_ID} onSubmit={handleSubmit}>
        <ToggleContainer>
          <ToggleHeaderContainer>
            <Text variant="subtitle">{tPlaceholder("Enable automation")}</Text>
            <Switch
              checked={enabled}
              disabled={isLoading}
              onChange={() => setEnabled(!enabled)}
            />
          </ToggleHeaderContainer>

          <Show if={enabled}>
            <Divider />

            <Text variant="subtitle">{tPlaceholder("Template")}</Text>

            <Text variant="bodyS">
              {tPlaceholder(
                "Select the template to use when this automation creates a task."
              )}
            </Text>

            <Select
              label={tPlaceholder("Template")}
              options={[emptyOption, ...templateOptions]}
              onChange={(value) => {
                setTemplateId(value)
              }}
              value={templateId ?? ""}
              disabled={!enabled || isLoading}
              required={enabled}
            />
          </Show>
        </ToggleContainer>
      </Form>
    </Dialog>
  )
}

const ToggleContainer = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const ToggleHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.S};
  align-items: center;
`

const Form = styled.form`
  display: grid;
  gap: ${spacing.M};
  padding-bottom: ${spacing.M};
`
